import React, { useState, useEffect } from "react";
import { FaRegEdit } from "react-icons/fa";
import { FaRegEye, FaEyeSlash } from "react-icons/fa";
import { IoMdArrowDropright, IoMdArrowDropup } from "react-icons/io";
import { getMerchantIPs, getUser } from "../../../config/LiveApi";
import { useSelector } from "react-redux";
import UpdateModal from "./UpdateModal";
import AddNewIp from "./AddNewIp";
import "./Ips.css";

const IpsTable = () => {
  const role = useSelector((store) => store.userReducer.role);
  const token = useSelector((store) => store.userReducer.token);
  const [isLoading, setIsLoading] = useState(true);
  const [merchantApikeys, setMerchantApikeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [expandedMerchants, setExpandedMerchants] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIpData, setSelectedIpData] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [search, setSearch] = useState("");

  const isAdminRole = role === "admin" || role === "subAdmin";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const merchantPromise = getMerchantIPs(token);
        const userPromise = isAdminRole ? null : getUser(token);
        const [merchantData, userData] = await Promise.all([
          merchantPromise,
          userPromise,
        ]);
        setMerchantApikeys(merchantData);
        if (userData) setUsers(userData.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token, role]);

  const getUserNameById = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user ? user.alias || user.id : "N/A";
  };

  const handleExpandToggle = (merchantId) => {
    setExpandedMerchants((prev) => ({
      ...prev,
      [merchantId]: !prev[merchantId],
    }));
  };

  const handleEditClick = (ip) => {
    setSelectedIpData(ip);
    setIsModalOpen(true);
  };

  const handleUpdateStatus = (ipId, newStatus) => {
    setMerchantApikeys((prev) =>
      prev.map((ip) => (ip.id === ipId ? { ...ip, ipStatus: newStatus } : ip))
    );
  };

  const groupIpsByMerchant = () => {
    return merchantApikeys.reduce((acc, api) => {
      if (!acc[api.userId]) acc[api.userId] = [];
      acc[api.userId].push(api);
      return acc;
    }, {});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusKey = (ipStatus) => {
    return ipStatus || "inactive";
  };

  const getStatusLabel = (ipStatus) => {
    const map = {
      active: "Enabled",
      inactive: "Disabled",
      created: "Created",
      blocked: "Blocked",
    };
    return map[ipStatus] || "Disabled";
  };

  const getStatusClass = (ipStatus) => {
    const key = getStatusKey(ipStatus);
    const map = {
      active: "ips-badge ips-badge-active",
      inactive: "ips-badge ips-badge-inactive",
      created: "ips-badge ips-badge-created",
      blocked: "ips-badge ips-badge-blocked",
    };
    return map[key] || "ips-badge ips-badge-inactive";
  };

  const getSummary = (ips) => {
    const counts = { active: 0, inactive: 0, created: 0, blocked: 0 };
    ips.forEach((ip) => {
      const key = getStatusKey(ip.ipStatus);
      if (counts[key] !== undefined) counts[key]++;
    });
    return counts;
  };

  const groupedIps = groupIpsByMerchant();

  const filteredMerchantIds = Object.keys(groupedIps).filter((id) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    const name = getUserNameById(id).toLowerCase();
    const ipMatch = groupedIps[id].some((ip) => ip.ip?.includes(s));
    return name.includes(s) || id.toLowerCase().includes(s) || ipMatch;
  });

  const totalMerchants = Object.keys(groupedIps).length;
  const totalIps = merchantApikeys.length;

  return (
    <div className="ips-page">
      <h1 className="ips-page-title">Merchant IP Whitelist</h1>

      <div className="ips-card">
        <div className="ips-toolbar">
          <div className="ips-search-wrap">
            <svg
              className="ips-search-icon"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              className="ips-search-input"
              placeholder="Search merchant, ID, or IP"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <span className="ips-meta-text">
            {totalMerchants} merchant{totalMerchants !== 1 ? "s" : ""} ·{" "}
            {totalIps} IP{totalIps !== 1 ? "s" : ""}
          </span>

          <button
            data-tour="server-ip"
            className="ips-btn-primary"
            onClick={() => setIsAddModalOpen(true)}
          >
            Whitelist merchant IP
          </button>
        </div>

        <div className="ips-table-scroll">
          <table className="ips-table">
            <thead className="ips-thead">
              <tr>
                {!isAdminRole && <th className="ips-th ips-th-icon"></th>}
                {!isAdminRole && <th className="ips-th ips-th-num">#</th>}
                {!isAdminRole && <th className="ips-th">Merchant name</th>}
                <th className="ips-th">IPs</th>
                <th className="ips-th ips-th-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="ips-empty-cell">
                    <div className="ips-loading">
                      <span className="ips-loading-dot" />
                      <span className="ips-loading-dot" />
                      <span className="ips-loading-dot" />
                    </div>
                  </td>
                </tr>
              ) : filteredMerchantIds.length === 0 ? (
                <tr>
                  <td colSpan="6" className="ips-empty-cell">
                    {search ? "No results found" : "No data available"}
                  </td>
                </tr>
              ) : isAdminRole ? (
                merchantApikeys.map((ip) => (
                  <tr key={ip.id} className="ips-merchant-row">
                    <td className="ips-td ips-td-mono">{ip.ip}</td>
                    <td className="ips-td">
                      <span className={getStatusClass(ip.ipStatus)}>
                        <span className="ips-badge-dot" />
                        {getStatusLabel(ip.ipStatus)}
                      </span>
                    </td>
                    <td className="ips-td ips-td-right">
                      {role === "super" && (
                        <button className="ips-edit-btn" onClick={() => handleEditClick(ip)}>
                          <FaRegEdit size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                filteredMerchantIds.map((merchantId, index) => {
                  const ips = groupedIps[merchantId];
                  const isExpanded = expandedMerchants[merchantId] || false;
                  const merchantName = getUserNameById(merchantId);
                  const hasSingleIp = ips.length === 1;
                  const summary = getSummary(ips);

                  return (
                    <React.Fragment key={merchantId}>
                      <tr
                        className={`ips-merchant-row ${isExpanded ? "ips-merchant-row-expanded" : ""}`}
                        onClick={() => !hasSingleIp && handleExpandToggle(merchantId)}
                      >
                        <td className="ips-td ips-td-icon">
                          {!hasSingleIp ? (
                            <button
                              className="ips-chevron-btn"
                              onClick={(e) => { e.stopPropagation(); handleExpandToggle(merchantId); }}
                              aria-label={isExpanded ? "Collapse" : "Expand"}
                            >
                              {isExpanded 
                              ? <IoMdArrowDropup size={18} />: <IoMdArrowDropright size={18} />}
                            </button>
                          ) : (
                            <div className="ips-chevron-placeholder" />
                          )}
                        </td>

                        <td className="ips-td">
                          <div className="ips-index-badge">{index + 1}</div>
                        </td>

                        <td className="ips-td ips-td-name">{merchantName}</td>

                        <td className="ips-td">
                          {hasSingleIp ? (
                            <span className="ips-td-mono">{ips[0].ip}</span>
                          ) : (
                            <div className="ips-summary-line">
                              <span className="ips-summary-count">{ips.length} IPs</span>
                              {summary.active > 0 && (
                                <span className="ips-summary-chip ips-chip-active">
                                  <span className="ips-summary-dot ips-dot-active" />
                                  {summary.active} enabled
                                </span>
                              )}
                              {summary.inactive > 0 && (
                                <span className="ips-summary-chip ips-chip-inactive">
                                  <span className="ips-summary-dot ips-dot-inactive" />
                                  {summary.inactive} disabled
                                </span>
                              )}
                              {summary.created > 0 && (
                                <span className="ips-summary-chip ips-chip-created">
                                  <span className="ips-summary-dot ips-dot-created" />
                                  {summary.created} created
                                </span>
                              )}
                              {summary.blocked > 0 && (
                                <span className="ips-summary-chip ips-chip-blocked">
                                  <span className="ips-summary-dot ips-dot-blocked" />
                                  {summary.blocked} blocked
                                </span>
                              )}
                            </div>
                          )}
                        </td>

                        <td className="ips-td ips-td-right">
                          {hasSingleIp ? (
                            <div className="ips-single-status">
                              <span className={getStatusClass(ips[0].ipStatus)}>
                                <span className="ips-badge-dot" />
                                {getStatusLabel(ips[0].ipStatus)}
                              </span>
                              {role === "super" && (
                                <button
                                  className="ips-edit-btn"
                                  onClick={(e) => { e.stopPropagation(); handleEditClick(ips[0]); }}
                                >
                                  <FaRegEdit size={15} />
                                </button>
                              )}
                            </div>
                          ) : (
                            <button
                              className="ips-view-btn"
                              onClick={(e) => { e.stopPropagation(); handleExpandToggle(merchantId); }}
                            >
                              {isExpanded ? <FaEyeSlash size={13} /> : <FaRegEye size={13} />}
                              {isExpanded ? "Hide IPs" : "View IPs"}
                            </button>
                          )}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="ips-subtable-row">
                          <td colSpan="6" className="ips-subtable-cell">
                            <table className="ips-subtable">
                              <thead>
                                <tr>
                                  <th className="ips-sub-th">IP address</th>
                                  <th className="ips-sub-th">Added</th>
                                  <th className="ips-sub-th">Status</th>
                                  {role === "super" && (
                                    <th className="ips-sub-th ips-th-right">Actions</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {ips.map((ip) => (
                                  <tr key={ip.id} className="ips-sub-row">
                                    <td className="ips-sub-td ips-td-mono">{ip.ip}</td>
                                    <td className="ips-sub-td ips-td-muted">{formatDate(ip.createdAt)}</td>
                                    <td className="ips-sub-td">
                                      <span className={getStatusClass(ip.ipStatus)}>
                                        <span className="ips-badge-dot" />
                                        {getStatusLabel(ip.ipStatus)}
                                      </span>
                                    </td>
                                    {role === "super" && (
                                      <td className="ips-sub-td ips-td-right">
                                        <button
                                          className="ips-edit-btn"
                                          onClick={() => handleEditClick(ip)}
                                        >
                                          <FaRegEdit size={15} />
                                        </button>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <UpdateModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          ipData={selectedIpData}
          token={token}
          onUpdate={handleUpdateStatus}
        />
      )}
      {isAddModalOpen && <AddNewIp onClose={() => setIsAddModalOpen(false)} />}
    </div>
  );
};

export default IpsTable;
